import { ChangeDetectionStrategy, Component, Input, OnChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Measurement, StageTreeNode } from '@cosmos/shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { PanelModule } from 'primeng/panel';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { MeasurementsService } from '@cosmos/angular/api';
import { ErrorService } from '@cosmos/angular/utils';
import { MultiLineChartComponent } from '../../data/charts/multi-line-chart/multi-line-chart.component';
import { ToggleFilterComponent } from '../toggle-filter/toggle-filter.component';
import { buildVariableCatalog } from './compare-catalog';
import { buildCompareLayout } from './compare-charts';
import { buildCompareTree } from './compare-tree';
import {
  COMPARE_TYPE_REGISTRY, CompareFilterDef, CompareFilterState, CompareMeasurementMeta, CompareTranslate,
} from './registry/compare-type-registry';

@Component({
  selector: 'cosmos-compare-measurements',
  imports: [
    DividerModule, ButtonModule, CardModule, CheckboxModule, FormsModule, PanelModule, TreeModule,
    MultiLineChartComponent, ToggleFilterComponent, TranslatePipe,
  ],
  templateUrl: './compare-measurements.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompareMeasurementsComponent implements OnChanges {

  @Input()
  stageTree: StageTreeNode[] = [];

  private readonly measurementsService = inject(MeasurementsService);
  private readonly errorService = inject(ErrorService);
  private readonly translateService = inject(TranslateService);

  readonly t: CompareTranslate = (key, params) => this.translateService.instant(key, params);

  // ── setup ────────────────────────────────────────────────────
  treeNodes: TreeNode[] = [];
  selectedNodes: TreeNode[] = [];
  readonly selectedMetas = signal<CompareMeasurementMeta[]>([]);
  readonly setupCatalog = computed(() => buildVariableCatalog(this.selectedMetas(), COMPARE_TYPE_REGISTRY));
  /** `${type}:${variableKey}` */
  readonly selectedVariables = signal<ReadonlySet<string>>(new Set());

  /** Compare is enabled only when a ticked variable still belongs to a ticked, supported measurement type. */
  readonly canCompare = computed(() => {
    const selected = this.selectedVariables();
    return this.setupCatalog().some((t) =>
      t.supported && t.sections.some((sec) => sec.variables.some((v) => selected.has(`${t.type}:${v.key}`))));
  });

  // ── results ──────────────────────────────────────────────────
  readonly loaded = signal<Measurement[]>([]);
  readonly loadingChart = signal(false);
  readonly hasLoaded = signal(false);
  /** The setup section collapses once a comparison is loaded, leaving the results in view. */
  setupCollapsed = false;
  /** measurementId → filterId → selected option values (missing ⇒ everything). */
  readonly filterState = signal<Record<string, CompareFilterState>>({});
  /** chartKey → overlay variable key (right-axis overlay chosen from the chart's menu). */
  readonly overlays = signal<Record<string, string | null>>({});

  readonly layout = computed(() => {
    const loaded = this.loaded();
    const catalog = buildVariableCatalog(loaded.map((m) => this.toMeta(m)), COMPARE_TYPE_REGISTRY);
    return buildCompareLayout(catalog, this.selectedVariables(), loaded, COMPARE_TYPE_REGISTRY, this.filterState(), this.t, this.overlays());
  });

  /** One row per loaded measurement that offers filters (per-measurement, shown together). */
  readonly filterRows = computed(() =>
    this.loaded().flatMap((m) =>
      (COMPARE_TYPE_REGISTRY[m.measurementType as string]?.filters?.(this.toMeta(m), this.t) ?? []).map((def) => ({
        measurementId: m._id as string,
        measurementName: m.name as string,
        def,
      })),
    ),
  );

  ngOnChanges(): void {
    this.treeNodes = buildCompareTree(this.stageTree);
    this.selectedNodes = [];
    this.selectedMetas.set([]);
    this.setupCollapsed = false;
  }

  toMeta(m: Measurement): CompareMeasurementMeta {
    return {
      _id: m._id as string,
      name: m.name as string,
      measurementType: m.measurementType as string,
      dataChannels: (m as any).dataChannels,
    };
  }

  onSelectionChange(nodes: TreeNode[]): void {
    this.selectedNodes = nodes ?? [];
    this.selectedMetas.set(
      this.selectedNodes.filter((n) => n.data?.type === 'measurement').map((n) => n.data.meta as CompareMeasurementMeta),
    );
  }

  isVariableSelected(type: string, key: string): boolean {
    return this.selectedVariables().has(`${type}:${key}`);
  }

  toggleVariable(type: string, key: string): void {
    const next = new Set(this.selectedVariables());
    const k = `${type}:${key}`;
    if (!next.delete(k)) next.add(k);
    this.selectedVariables.set(next);
  }

  filterSelection(measurementId: string, def: CompareFilterDef): string[] {
    return this.filterState()[measurementId]?.[def.id] ?? def.options.map((o) => o.value);
  }

  onFilterChange(measurementId: string, filterId: string, selection: string[]): void {
    this.filterState.update((s) => ({ ...s, [measurementId]: { ...(s[measurementId] ?? {}), [filterId]: selection } }));
  }

  onOverlayChange(chartKey: string, variableKey: string | null): void {
    this.overlays.update((o) => ({ ...o, [chartKey]: variableKey }));
  }

  submit(): void {
    const supported = this.selectedMetas().filter((m) => !!COMPARE_TYPE_REGISTRY[m.measurementType]);
    if (!supported.length) {
      this.loaded.set([]);
      this.hasLoaded.set(false);
      return;
    }
    const select = [...new Set(supported.flatMap((m) => COMPARE_TYPE_REGISTRY[m.measurementType].selectFields))];

    this.loadingChart.set(true);
    this.measurementsService
      .find(`?_id=${supported.map((m) => m._id).join(',')}&select=${select.join(',')}`)
      .subscribe({
        next: (res) => {
          this.loaded.set((res.body as Measurement[]) ?? []);
          this.filterState.set({});
          this.overlays.set({});
          this.hasLoaded.set(true);
          this.setupCollapsed = true;
          this.loadingChart.set(false);
        },
        error: (e) => {
          this.errorService.handleError(e);
          this.loadingChart.set(false);
        },
      });
  }
}
